import { createFileRoute } from "@tanstack/react-router";
import {
  IndustryMarketingPage,
  industryMarketingHead,
  type IndustryFAQ,
} from "@/components/IndustryMarketingPage";

const FAQS: IndustryFAQ[] = [
  {
    q: "How do I get more concrete jobs in Colorado Springs?",
    a: "The fastest path is a tuned Google Business Profile plus local SEO ranking you for 'concrete contractor near me', 'concrete patio Colorado Springs', and 'concrete driveway Denver', backed by a website with photos of your actual finished work. We pair this with Google Local Service Ads in spring and summer when patio and driveway demand peaks. See our full guide: /answers/how-to-get-more-concrete-jobs-colorado.",
  },
  {
    q: "What's the best marketing channel for a Colorado concrete contractor?",
    a: "Local SEO + Google Business Profile is the highest-ROI channel for most Colorado concrete contractors because the category is undermarketed. Google Ads works for spring/summer patio and driveway demand. Most concrete contractors don't need social media or content marketing — review velocity and photos do the heavy lifting.",
  },
  {
    q: "How long does concrete contractor SEO take to work?",
    a: "Most Colorado concrete contractors see meaningful Map Pack movement in 60–120 days. Competitive organic terms like 'concrete contractor Colorado Springs' typically take 4–6 months of consistent review velocity, fresh photos, and on-page work.",
  },
  {
    q: "How do I rank in the Google Map Pack for concrete in Colorado?",
    a: "Three things matter most: a fully optimized Google Business Profile (Concrete Contractor + Foundation Contractor + Paving Contractor categories), a steady stream of real Google reviews after every pour, and city-specific service pages for every market you actually serve.",
  },
  {
    q: "Can you help with stamped concrete and decorative work specifically?",
    a: "Yes. Stamped concrete, exposed aggregate, and decorative flatwork are higher-margin specialties most contractors bury in their service list. We build dedicated pages targeting those specific searches so the right buyers find you.",
  },
];

export const Route = createFileRoute("/concrete-marketing-colorado")({
  head: () =>
    industryMarketingHead("Concrete Contractor", "concrete contractor", "concrete-marketing-colorado", FAQS),
  component: () => (
    <IndustryMarketingPage
      industry="Concrete Contractor"
      industryLower="concrete contractor"
      slug="concrete-marketing-colorado"
      subheadline="We help Colorado concrete contractors fill the spring and summer schedule — patios, driveways, foundations, and stamped work."
      painPoints={[
        "Word-of-mouth carries the winter but spring demand goes to whoever ranks on Google",
        "GBP is half-finished and reviews are stuck in the teens",
        "Stamped, decorative, and high-margin work is buried in a generic services list",
      ]}
      serviceDescriptions={{
        seo: "Rank for patios, driveways, foundations, retaining walls, and stamped concrete across every Colorado market you serve.",
        googleMaps: "Dominate the local Map Pack so 'concrete contractor near me' calls come to your shop first.",
        webDesign: "Fast, mobile-first contractor sites with real job photo galleries — not stock images.",
        ppc: "Profitable Google Ads and Local Service Ads timed to spring and summer patio demand.",
        geoAi: "Get cited when Coloradans ask ChatGPT 'best concrete contractor near me' or 'how much does a stamped patio cost'.",
        socialMedia: "Real job-site content (pours, finishes, reveals) that builds trust faster than any sales page.",
      }}
      testimonial={{
        quote:
          "Spring used to be a scramble. Now we're booked through July before the snow melts. Best investment we've made in this business.",
        role: "Concrete Contractor",
        city: "Colorado Springs",
      }}
      faqs={FAQS}
      cityHubLinks={[
        { to: "/colorado-springs", label: "Concrete marketing in Colorado Springs", description: "Help Springs flatwork shops own the Map Pack across Briargate, Powers, and Black Forest." },
        { to: "/denver-digital-marketing", label: "Concrete marketing in Denver", description: "Compete in Denver's saturated patio and driveway market — neighborhood by neighborhood." },
        { to: "/pueblo-digital-marketing", label: "Concrete marketing in Pueblo", description: "Foundation, flatwork, and freeze-thaw repair across southern Colorado." },
      ]}
    />
  ),
});
